import { Academic, Address, Alumni, AlumniExtended, Business, WebUser } from "iasma-datatypes";
import { ArrowLeft, ArrowUp, Check, Plus, Trash } from "lucide-react";
import { useMemo, useState } from "react";

/* ------------------------------------------------------------------ */
/*  Diff helpers                                                       */
/* ------------------------------------------------------------------ */

type WithId = { Id: number };

function stripId<T extends WithId>(item: T): Omit<T, "Id"> {
	const { Id, ...rest } = item;
	return rest;
}

function sameContent<T extends WithId>(a: T, b: T): boolean {
	return JSON.stringify(stripId(a)) === JSON.stringify(stripId(b));
}

function findMatch<T extends WithId>(item: T, list: T[]): T | undefined {
	return list.find((candidate) => sameContent(candidate, item));
}

function cloneRecord(rec: AlumniExtended): AlumniExtended {
	return JSON.parse(JSON.stringify(rec));
}

/* ------------------------------------------------------------------ */
/*  Presentational primitives                                          */
/* ------------------------------------------------------------------ */

function ValuePane({ status, children, }: {
	status: "match" | "differ" | "neutral";
	children: React.ReactNode;
}) {
	const styles =
		status === "differ"
			? "border-(--flag)/40 bg-(--flag-bg)"
			: status === "match"
				? "border-(--match)/30 bg-(--match-bg)"
				: "border-(--rule) bg-(--paper-raised)";
	return (
		<div className={`${styles} rounded-sm border px-3.5 py-2.5 text-[13.5px] leading-relaxed text-(--ink)`}>
			{children}
		</div>
	);
}

function SectionHeading({ title, note, }: {
	title: string;
	note?: string;
}) {
	return (
		<div className="flex items-baseline justify-between border-b border-(--rule) pb-2 mb-4">
			<h2
				className="text-[19px] tracking-tight text-(--ink)"
				style={{ fontFamily: "var(--serif)" }}
			>
				{title}
			</h2>
			{note && (
				<span
					className="text-[11.5px] text-(--ink-soft)"
					style={{ fontFamily: "var(--mono)" }}
				>
					{note}
				</span>
			)}
		</div>
	);
}

/* ------------------------------------------------------------------ */
/*  Scalar field row (name / birthdate / description)                  */
/* ------------------------------------------------------------------ */

function EditableValue({
	status,
	value,
	onChange,
	multiline = false,
	inputType = "text",
}: {
	status: "match" | "differ";
	value: string;
	onChange: (v: string) => void;
	multiline?: boolean;
	inputType?: "text" | "date";
}) {
	const styles =
		status === "differ"
			? "border-(--flag)/50 bg-(--flag-bg) focus:border-(--flag)"
			: "border-(--match)/40 bg-(--match-bg) focus:border-(--match)";
	const shared = `w-full rounded-sm border px-3.5 py-2.5 text-[13.5px] leading-relaxed text-(--ink) outline-none transition-colors ${styles}`;

	if (multiline) {
		return (
			<textarea
				value={value}
				onChange={(e) => onChange(e.target.value)}
				rows={3}
				className={`${shared} resize-y`}
			/>
		);
	}
	return (
		<input
			type={inputType}
			value={value}
			onChange={(e) => onChange(e.target.value)}
			className={shared}
		/>
	);
}

function ScalarRow({ label, mainValue, compareValue, onMainChange, onReplace, onMerge, multiline = false, inputType = "text", }: {
	label: string;
	mainValue: string;
	compareValue: string | undefined;
	onMainChange: (v: string) => void;
	onReplace: () => void;
	onMerge?: () => void;
	multiline?: boolean;
	inputType?: "text" | "date";
}) {
	const differs = mainValue !== compareValue;

	return (
		<div className="grid grid-cols-1 lg:grid-cols-[1fr_128px_1fr] gap-2.5 lg:gap-4 items-stretch py-3 border-b border-(--rule) last:border-b-0">
			<div className="flex flex-col gap-1">
				<span
					className="text-[10.5px] uppercase-none text-(--ink-soft)"
					style={{ fontFamily: "var(--mono)" }}
				>
					{label}
				</span>
				<EditableValue
					status={differs ? "differ" : "match"}
					value={mainValue}
					onChange={onMainChange}
					multiline={multiline}
					inputType={inputType}
				/>
			</div>

			<div className="flex lg:flex-col items-center justify-center lg:justify-start gap-1.5 lg:gap-2 py-1 lg:pt-6 flex-wrap">
				{compareValue ?
					differs ? (
						<>
							<button
								onClick={onReplace}
								className="group flex items-center gap-1.5 rounded-sm border border-(--flag) bg-(--paper-raised) px-2.5 py-1.5 text-[11.5px] text-(--flag) transition-colors hover:bg-(--flag) hover:text-(--paper-raised)"
								style={{ fontFamily: "var(--mono)" }}
								title="Adopt the compare-side value into Main"
							>
								<span className="inline-block lg:hidden">
									<ArrowUp className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5" />
								</span>
								<span className="hidden lg:inline-block">
									<ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
								</span>
								timpa
							</button>
							{onMerge && (
								<button
									onClick={onMerge}
									className="flex items-center gap-1.5 rounded-sm border border-(--navy) bg-(--paper-raised) px-2.5 py-1.5 text-[11.5px] text-(--navy) transition-colors hover:bg-(--navy) hover:text-(--paper-raised)"
									style={{ fontFamily: "var(--mono)" }}
									title="Merge both values into a comma-separated value"
								>
									<span className="inline-block lg:hidden"><ArrowUp className="h-3.5 w-3.5" /></span>
									<span className="hidden lg:inline-block"><ArrowLeft className="h-3.5 w-3.5" /></span>
									gabung
								</button>
							)}
						</>
					) : (
						<span
							className="flex items-center gap-1 text-[11px] text-(--match) lg:translate-y-1.5"
							style={{ fontFamily: "var(--mono)" }}
						>
							<Check className="h-3.5 w-3.5" />
							identik
						</span>
					)
					: <></>}
			</div>

			{compareValue ?
				<div className="flex flex-col gap-1">
					<span
						className="text-[10.5px] text-(--ink-soft) lg:text-right"
						style={{ fontFamily: "var(--mono)" }}
					>
						{label}
					</span>
					<ValuePane status={differs ? "differ" : "neutral"}>{compareValue}</ValuePane>
				</div>
				: <></>}
		</div>
	);
}

/* ------------------------------------------------------------------ */
/*  Generic list-field section (addresses / academics / business)       */
/* ------------------------------------------------------------------ */

interface ArraySectionProps<T extends WithId> {
	title: string;
	mainItems: T[];
	compareItems: T[];
	renderSummary: (item: T) => React.ReactNode;
	renderEditable: (item: T, onChange: (patch: Partial<T>) => void) => React.ReactNode;
	onMergeSelected: (items: T[]) => void;
	onReplaceAll: () => void;
	onRemoveFromMain: (id: number) => void;
	onUpdateItem: (id: number, patch: Partial<T>) => void;
	onAddBlank: () => void;
	addLabel: string;
}

function ArraySection<T extends WithId>({ title, mainItems, compareItems, renderSummary, renderEditable, 
	onMergeSelected, onReplaceAll, onRemoveFromMain, onUpdateItem, onAddBlank, addLabel, 
}: ArraySectionProps<T>) {
	const [selected, setSelected] = useState<Set<number>>(new Set());

	const newFromCompare = useMemo(
		() => compareItems.filter((c) => !findMatch(c, mainItems)),
		[compareItems, mainItems]
	);

	const matchedIds = useMemo(
		() => new Set(compareItems.filter((c) => findMatch(c, mainItems)).map((c) => c.Id)),
		[compareItems, mainItems]
	);

	function toggle(id: number) {
		setSelected((prev) => {
			const next = new Set(prev);
			next.has(id) ? next.delete(id) : next.add(id);
			return next;
		});
	}

	function mergeSelected() {
		const items = newFromCompare.filter((i) => selected.has(i.Id));
		if (items.length === 0) return;
		onMergeSelected(items);
		setSelected(new Set());
	}

	const diffCount = newFromCompare.length;

	return (
		<section className="mb-9">
			<SectionHeading
				title={title}
				note={
					diffCount > 0
						? `${diffCount} new on other side`
						: "no new items"
				}
			/>

			<div className="grid grid-cols-1 lg:grid-cols-[1fr_128px_1fr] gap-4">
				{/* MAIN column — directly editable */}
				<div className="flex flex-col gap-2">
					{mainItems.length === 0 && (
						<p className="text-[13px] text-(--ink-soft) italic">Empty.</p>
					)}
					{mainItems.map((item) => (
						<div
							key={item.Id}
							className="group relative rounded-sm border border-(--rule) bg-(--paper-raised) px-3.5 py-3"
						>
							<div className="pr-6">
								{renderEditable(item, (patch) => onUpdateItem(item.Id, patch))}
							</div>
							<button
								onClick={() => onRemoveFromMain(item.Id)}
								className="absolute right-2 top-2 text-(--ink-soft) opacity-0 transition-opacity hover:text-(--flag) group-hover:opacity-100"
								title="Remove from main"
								aria-label="Remove from main"
							>
								<Trash className="h-4 w-4" />
							</button>
						</div>
					))}
					<button
						onClick={onAddBlank}
						className="flex items-center justify-center gap-1.5 rounded-sm border border-dashed border-(--rule) px-3 py-2 text-[11.5px] text-(--ink-soft) transition-colors hover:border-(--navy) hover:text-(--navy)"
						style={{ fontFamily: "var(--mono)" }}
					>
						<Plus className="h-3.5 w-3.5" />
						{addLabel}
					</button>
				</div>

				{/* CONTROLS column */}
				<div className="flex lg:flex-col items-center justify-start gap-2 py-1 flex-wrap">
					<button
						onClick={mergeSelected}
						disabled={selected.size === 0}
						className="flex items-center gap-1.5 rounded-sm border border-(--navy) bg-(--paper-raised) px-2.5 py-1.5 text-[11.5px] text-(--navy) transition-colors enabled:hover:bg-(--navy) enabled:hover:text-(--paper-raised) disabled:cursor-not-allowed disabled:opacity-35"
						style={{ fontFamily: "var(--mono)" }}
						title="Add the checked compare-side items to Main"
					>
						<span className="inline-block lg:hidden"><ArrowUp className="h-3.5 w-3.5" /></span>
						<span className="hidden lg:inline-block"><ArrowLeft className="h-3.5 w-3.5" /></span>
						gabung ({selected.size})
					</button>
					<button
						onClick={onReplaceAll}
						disabled={newFromCompare.length === 0}
						className="group flex items-center rounded-sm border border-(--flag) bg-(--paper-raised) px-2.5 py-1.5 text-[11.5px] text-(--flag) transition-colors enabled:hover:bg-(--flag) enabled:hover:text-(--paper-raised) disabled:cursor-not-allowed disabled:opacity-35"
						style={{ fontFamily: "var(--mono)" }}
						title="Replace Main's entire list with the compare-side list"
					>
						<span className="inline-block lg:hidden">
							<ArrowUp className="h-3.5 w-3.5 transition-transform group-hover:-translate-y-0.5" />
						</span>
						<span className="hidden lg:inline-block">
							<ArrowLeft className="h-3.5 w-3.5 transition-transform group-hover:-translate-x-0.5" />
						</span>
						timpa
					</button>
				</div>

				{/* COMPARE column */}
				<div className="flex flex-col gap-2">
					{compareItems.length === 0 && (
						<p className="text-[13px] text-(--ink-soft) italic">Empty.</p>
					)}
					{compareItems.map((item) => {
						const isMatched = matchedIds.has(item.Id);
						return (
							<label
								key={item.Id}
								className={`flex items-start gap-2.5 rounded-sm border px-3.5 py-2.5 ${isMatched
									? "border-(--match)/30 bg-(--match-bg)"
									: "border-(--flag)/40 bg-(--flag-bg) cursor-pointer"
									}`}
							>
								{isMatched ? (
									<Check className="mt-0.5 h-4 w-4 shrink-0 text-(--match)" />
								) : (
									<input
										type="checkbox"
										checked={selected.has(item.Id)}
										onChange={() => toggle(item.Id)}
										className="mt-1 h-3.5 w-3.5 shrink-0 accent-(--flag)"
									/>
								)}
								<div className="flex-1">{renderSummary(item)}</div>
							</label>
						);
					})}
				</div>
			</div>
		</section>
	);
}

function stringifyNoQuotesPretty(obj: any) {
	return Object.entries(obj)
		.map(([key, value]) => `${key}: ${JSON.stringify(value)}`)
		.join('\n');
}

function showHistoryValue(key: string, value: any): React.ReactNode {
	let newValue: React.ReactNode = String(value);
	let isFlexRow = true;
	if (value && typeof value === "object") {
		try {
			newValue = <pre>{stringifyNoQuotesPretty(value)}</pre>;
			isFlexRow = false;
		} catch (error) {
		}
	}
	if (value && value.XD && typeof value.XD === "object") {
		try {
			newValue = <pre>{stringifyNoQuotesPretty(value.XD)}</pre>;
			isFlexRow = false;
		} catch (error) {
		}
	}
	return <div className={`mt-1 text-sm flex ${isFlexRow ? 'flex-row' : 'flex-col'} gap-0.5`} key={key}>
		<div className="text-slate-400">
			{key}:
		</div>
		<div className={`text-slate-800 font-semibold wrap-anywhere ${isFlexRow ? 'ml-1' : 'ml-5'}`}>
			{newValue}
		</div>
	</div>
}

function renderHistory(xd: any): React.ReactNode[] {
	let elements: React.ReactNode[] = []
	let count = 0
	if (!xd || !xd.History) {
		elements.push(<div className="text-sm text-gray-500">Tidak ada riwayat</div>);
		console.log("no-xd", typeof xd ,xd)
	} else {
		let idx = 0;
		let keys = Object.keys(xd.History).sort((a, b) => b.localeCompare(a));
		count = keys.length;
		for (const key of keys) {
			const item = xd.History[key];
			elements.push(<div key={idx} className="relative">
				<div className="absolute -left-6.25 top-1.5 h-2.5 w-2.5 rounded-full border-2 border-white bg-slate-400 ring-1 ring-blue-600" />
				<div className="text-sm font-semibold text-blue-600">
					{key}
				</div>
				{Object.entries(item).map(([keyx, valuex]) => (
					showHistoryValue(keyx, valuex)
				))}
			</div>);
			idx++;
		}
	}
	return elements;
}

function HistorySection({ title, mainXD, otherXD, onMerge }: {
	title: string;
	mainXD: any;
	otherXD: any;
	onMerge: () => void;
}) {
	let mainElements = renderHistory(mainXD);
	let otherElements = renderHistory(otherXD);
	return (
		<section className="mb-9">
			<SectionHeading
				title={title}
				note={""}
			/>

			<div className="grid grid-cols-1 lg:grid-cols-[1fr_128px_1fr] gap-4">
				{/* MAIN column — directly editable */}
				<div className="flex flex-col gap-2">
					{mainElements}
				</div>

				{/* CONTROLS column */}
				<div className="flex lg:flex-col items-center justify-start gap-2 py-1 flex-wrap">
					<button
						onClick={onMerge}
						disabled={Object.keys(otherXD).length === 0}
						className="flex items-center rounded-sm border border-(--flag) bg-(--paper-raised) px-2.5 py-1.5 text-[11.5px] text-(--flag) transition-colors enabled:hover:bg-(--flag) enabled:hover:text-(--paper-raised) disabled:cursor-not-allowed disabled:opacity-35"
						style={{ fontFamily: "var(--mono)" }}
						title="Replace Main's entire list with the compare-side list"
					>
						<span className="inline-block lg:hidden"><ArrowUp className="h-3.5 w-3.5" /></span>
						<span className="hidden lg:inline-block"><ArrowLeft className="h-3.5 w-3.5" /></span>
						gabung
					</button>
				</div>

				{/* COMPARE column */}
				<div className="flex flex-col gap-2">
					{otherElements}
				</div>
			</div>
		</section>
	);
}

/* ------------------------------------------------------------------ */
/*  Item renderers                                                     */
/* ------------------------------------------------------------------ */

function MiniField({ label, placeholder, value, onChange, type = "text", span = 1, }: {
	label: string;
	placeholder?: string;
	value: string | number;
	onChange?: (v: string) => void;
	type?: "text" | "number";
	span?: 1 | 2;
}) {
	return (
		<label className={`flex flex-col gap-0.5 ${span === 2 ? "col-span-2" : "col-span-1"}`} >
			<span className="text-[9.5px] uppercase-none text-(--ink-soft)" style={{ fontFamily: "var(--mono)" }} >
				{label}
			</span>
			<input
				placeholder={placeholder}
				type={type}
				value={value}
				readOnly={onChange ? false : true}
				onChange={onChange ? (e) => onChange(e.target.value) : undefined}
				className="w-full rounded-sm border border-(--rule) bg-(--paper) px-2 py-1.5 text-[12.5px] text-(--ink) outline-none focus:border-(--navy)"
			/>
		</label>
	);
}

function renderAddress(a: Address) {
	return (
		<div className="grid grid-cols-2 gap-2">
			<MiniField label="label" value={a.Kind} span={2} placeholder="rumah/kampung/lainnya" />
			<MiniField label="Jalan/Gedung" value={a.Street} span={2} placeholder="nama lokasi, nomor rumah" />
			<MiniField label="Kelurahan" value={a.Village} />
			<MiniField label="Kecamatan" value={a.District} />
			<MiniField label="Kota/Kabupaten" value={a.City} />
			<MiniField label="Provinsi" value={a.State} />
			<MiniField label="Kode POS" value={a.ZipCode} />
			<MiniField label="Negara" value={a.Country} />
		</div>
	);
}

function renderAddressEditable(a: Address, onChange: (patch: Partial<Address>) => void) {
	return (
		<div className="grid grid-cols-2 gap-2">
			<MiniField label="label" value={a.Kind} onChange={(v) => onChange({ Kind: v })} span={2} placeholder="rumah/kampung/lainnya" />
			<MiniField label="Jalan/Gedung" value={a.Street} onChange={(v) => onChange({ Street: v })} span={2} placeholder="nama lokasi, nomor rumah" />
			<MiniField label="Kelurahan" value={a.Village} onChange={(v) => onChange({ Village: v })} />
			<MiniField label="Kecamatan" value={a.District} onChange={(v) => onChange({ District: v })} />
			<MiniField label="Kota/Kabupaten" value={a.City} onChange={(v) => onChange({ City: v })} />
			<MiniField label="Provinsi" value={a.State} onChange={(v) => onChange({ State: v })} />
			<MiniField label="Kode POS" value={a.ZipCode} onChange={(v) => onChange({ ZipCode: v })} />
			<MiniField label="Negara" value={a.Country} onChange={(v) => onChange({ Country: v })} />
		</div>
	);
}

function makeBlankAddress(destination: AlumniExtended): Address {
	return {
		Id: destination.Addresses.length + 1,
		Kind: "",
		Street: "",
		City: "",
		State: "",
		ZipCode: "",
		Country: "",
		District: "",
		Village: "",
	};
}

function renderAcademic(a: Academic) {
	return (
		<div className="grid grid-cols-2 gap-2">
			<MiniField label="Kampus/Institusi" value={a.Institution} span={2} />
			<MiniField label="Degree" value={a.Degree} />
			<MiniField label="Bidang Studi" value={a.Major} />
			<MiniField
				label="Tahun"
				type="text"
				value={a.Years}
			/>
		</div>
	);
}

function renderAcademicEditable(a: Academic, onChange: (patch: Partial<Academic>) => void) {
	return (
		<div className="grid grid-cols-2 gap-2">
			<MiniField label="Kampus/Institusi" value={a.Institution} onChange={(v) => onChange({ Institution: v })} span={2} />
			<MiniField label="Strata" value={a.Degree} onChange={(v) => onChange({ Degree: v as Academic["Degree"] })} />
			<MiniField label="Bidang Studi" value={a.Major} onChange={(v) => onChange({ Major: v })} />
			<MiniField
				label="Tahun"
				type="text"
				value={a.Years}
				onChange={(v) => onChange({ Years: v })}
			/>
		</div>
	);
}

function makeBlankAcademic(destination: AlumniExtended): Academic {
	return {
		Id: destination.Academics.length + 1,
		Institution: "",
		Degree: "S1",
		Major: "",
		Years: "",
	};
}

function renderBusiness(b: Business) {
	return (
		<div className="grid grid-cols-2 gap-2">
			<MiniField label="Institution" value={b.Company} span={2} />
			<MiniField label="Bidang" value={b.Category} />
			<MiniField label="Spesialisasi" value={b.Specialty} />
			<MiniField label="Jabatan" value={b.Title} />
			<MiniField label="Periode" value={b.Periode} />
			<MiniField label="Keterangan" value={b.Description} span={2} />
			<MiniField label="Masih Aktif" value={String(b.IsActive)} />
		</div>
	);
}

function renderBusinessEditable(
	b: Business,
	onChange: (patch: Partial<Business>) => void
) {
	return (
		<div className="grid grid-cols-2 gap-2">
			<MiniField label="Institution" value={b.Company} onChange={(v) => onChange({ Company: v })} span={2} />
			<MiniField label="Bidang" value={b.Category} onChange={(v) => onChange({ Category: v })} />
			<MiniField label="Spesialisasi" value={b.Specialty} onChange={(v) => onChange({ Specialty: v })} />
			<MiniField label="Jabatan" value={b.Title} onChange={(v) => onChange({ Title: v })} />
			<MiniField label="Periode" value={b.Periode} onChange={(v) => onChange({ Periode: v })} />
			<MiniField label="Keterangan" value={b.Description} onChange={(v) => onChange({ Description: v })} span={2} />
			<MiniField label="Masih Aktif" value={String(b.IsActive)} onChange={(v) => onChange({ IsActive: Boolean(v) })} />
		</div>
	);
}

function makeBlankBusiness(destination: AlumniExtended): Business {
	const now = new Date().toISOString();
	return {
		Id: destination.Businesses.length + 1,
		Category: "",
		Title: "",
		Company: "",
		Specialty: "",
		Periode: "",
		IsActive: true,
		Description: "",
	};
}

function renderWebUsers(wu: WebUser) {
	return (
		<div className="grid grid-cols-2 gap-2">
			<MiniField label="Nama" value={wu.Name} span={2} />
			<MiniField label="Email" value={wu.Email} />
		</div>
	);
}

function renderWebUsersEditable(wu: WebUser, onChange: (patch: Partial<WebUser>) => void
) {
	return (
		<div className="grid grid-cols-2 gap-2">
			<MiniField label="Nama" value={wu.Name} onChange={(v) => onChange({ Name: v })} />
			<MiniField label="Email" value={wu.Email} onChange={(v) => onChange({ Email: v })} />
		</div>
	);
}

/* ------------------------------------------------------------------ */
/*  JSON edit panel                                                     */
/* ------------------------------------------------------------------ */
function JsonEditPanel({ main, other, onApply }: {
	main: AlumniExtended;
	other: AlumniExtended;
	onApply: (main: AlumniExtended, other: AlumniExtended) => void;
}) {
	const [mainText, setMainText] = useState(JSON.stringify(main, null, 2));
	const [otherText, setOtherText] = useState(JSON.stringify(other, null, 2));

	return (
		<div className="mb-8 rounded-sm border border-(--rule) bg-(--paper-raised) p-4">
			<div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
				<div>
					<label
						className="mb-1.5 block text-[10.5px] text-(--ink-soft)"
						style={{ fontFamily: "var(--mono)" }}
					>
						<b>main</b> record json
					</label>
					<textarea
						value={mainText}
						onChange={(e) => setMainText(e.target.value)}
						spellCheck={false}
						rows={10}
						className="w-full resize-y rounded-sm border border-(--rule) bg-(--paper) p-3 text-[12px] text-(--ink) outline-none focus:border-(--navy)"
						style={{ fontFamily: "var(--mono)" }}
					/>
				</div>
				<div>
					<label
						className="mb-1.5 block text-[10.5px] text-(--ink-soft)"
						style={{ fontFamily: "var(--mono)" }}
					>
						<b>other</b> record json
					</label>
					<textarea
						value={otherText}
						onChange={(e) => setOtherText(e.target.value)}
						spellCheck={false}
						rows={10}
						className="w-full resize-y rounded-sm border border-(--rule) bg-(--paper) p-3 text-[12px] text-(--ink) outline-none focus:border-(--navy)"
						style={{ fontFamily: "var(--mono)" }}
					/>
				</div>
			</div>
		</div>
	);
}

/* ------------------------------------------------------------------ */
/*  Design tokens (ledger palette)                                     */
/* ------------------------------------------------------------------ */
const tokens: React.CSSProperties = {
	["--paper" as any]: "#F7F5EF",
	["--paper-raised" as any]: "#FFFFFF",
	["--ink" as any]: "#20261F",
	["--ink-soft" as any]: "#5B6058",
	["--rule" as any]: "#D8D2C0",
	["--flag" as any]: "#B5541F",
	["--flag-bg" as any]: "#F3E3D6",
	["--match" as any]: "#3F6C4F",
	["--match-bg" as any]: "#E4EDE2",
	["--navy" as any]: "#233B52",
	["--navy-bg" as any]: "#E4E9EE",
	["--serif" as any]: "'Source Serif 4', 'Iowan Old Style', Georgia, 'Times New Roman', serif",
	["--mono" as any]: "'IBM Plex Mono', ui-monospace, SFMono-Regular, Menlo, Consolas, monospace",
};


export default function ViewMerge({ main, other, onMerge, onCancel }: {
	main: AlumniExtended,
	other: AlumniExtended,
	onMerge?: (original: AlumniExtended, tobeKeep: AlumniExtended, tobeDeleted: AlumniExtended) => void;
	onCancel?: () => void;
}) {
	const [merging, setMerging] = useState<AlumniExtended>(main);
	const [showEditor, setShowEditor] = useState(false);
	const [showResultJson, setShowResultJson] = useState(false);

	const scalarDiffs = useMemo(() => {
		let n = 0;
		if (merging.Name !== other.Name) n++;
		if (merging.BirthDate !== other.BirthDate) n++;
		if (merging.Classes !== other.Classes) n++;
		return n;
	}, [merging, other]);

	const newAddressCount = useMemo(
		() => other.Addresses.filter((c) => !findMatch(c, merging.Addresses)).length,
		[other.Addresses, merging.Addresses]
	);
	const newAcademicCount = useMemo(
		() => other.Academics.filter((c) => !findMatch(c, merging.Academics)).length,
		[other.Academics, merging.Academics]
	);
	const newBusinessCount = useMemo(
		() => other.Businesses.filter((c) => !findMatch(c, merging.Businesses)).length,
		[other.Businesses, merging.Businesses]
	);

	const totalDiffs = scalarDiffs + newAddressCount + newAcademicCount + newBusinessCount;

	function copyScalar(field: "Name" | "Gender" | "Religion" | "NIS" | "Classes" | "Alias" | "Title" | "BirthDate" | "DOD" | "Phones" | "Privacies") {
		setMerging((prev) => ({ ...prev, [field]: other[field] }));
	}

	function mergePhones() {
		setMerging((prev) => {
			const phones = [prev.Phones, other.Phones]
				.flatMap((value) => value?.split(",") ?? [])
				.map((value) => value.trim())
				.filter(Boolean);
			return { ...prev, Phones: [...new Set(phones)].join(", ") };
		});
	}

	function mergeAddresses(items: Address[]) {
		setMerging((prev) => ({
			...prev,
			Addresses: [...prev.Addresses, ...items.map((i) => ({ ...i, Id: i.Id }))],
		}));
	}

	function mergeAcademics(items: Academic[]) {
		setMerging((prev) => ({
			...prev,
			Academics: [...prev.Academics, ...items.map((i) => ({ ...i, Id: i.Id }))],
		}));
	}

	function mergeBusiness(items: Business[]) {
		setMerging((prev) => ({
			...prev,
			Businesses: [...prev.Businesses, ...items.map((i) => ({ ...i, Id: i.Id }))],
		}));
	}

	function mergeWebUsers(items: WebUser[]) {
		setMerging((prev) => ({
			...prev,
			WebUsers: [...prev.WebUsers, ...items.map((i) => ({ ...i, Id: i.Id }))],
		}));
	}

	function mergeHistory() {
		setMerging((prev) => {
			const mainExtraData = prev.ExtraData;
			const otherExtraData = other.ExtraData;
			const history = {
				...(mainExtraData?.History ?? {}),
				...(otherExtraData?.History ?? {}),
			};
			const sortedHistory = Object.fromEntries(
				Object.entries(history).sort(([left], [right]) => right.localeCompare(left))
			);

			other.ExtraData = {} // to make merge button disable
			return {
				...prev,
				ExtraData: {
					...mainExtraData,
					...otherExtraData,
					History: sortedHistory,
				},
			};
		});
	}

	function resetMerging() {
		// setMerging(cloneRecord(main));
		setMerging(main)
	}

	return (
		<div
			style={tokens}
			className="min-h-screen bg-(--paper) px-4 py-8 sm:px-8 lg:px-12"
		>
			<div className="mx-auto max-w-5xl">
				{/* Header */}
				<header className="mb-8 flex flex-col gap-4 border-b border-(--rule) pb-6 sm:flex-row sm:items-end sm:justify-between">
					<div>
						<h1
							className="text-[28px] leading-tight text-(--ink)"
							style={{ fontFamily: "var(--serif)" }}
						>
							Perbandingan Record
						</h1>
						<p className="mt-1 text-[13.5px] text-(--ink-soft)">
							{totalDiffs === 0
								? "Kedua data sama pada semua field."
								: `${totalDiffs} field${totalDiffs === 1 ? "" : "s"} `}
							perbedaan antara data <b>Utama</b> (Main) dan data <b>Lainnya</b> (Other).
						</p>
					</div>
					<div className="flex flex-wrap gap-2">
						<button
							onClick={() => setShowEditor((v) => !v)}
							className="rounded-sm border border-(--rule) bg-(--paper-raised) px-3 py-1.5 text-[12px] text-(--ink) transition-colors hover:border-(--navy)"
							style={{ fontFamily: "var(--mono)" }}
						>
							{showEditor ? "tutup data raw" : "lihat data raw"}
						</button>
						<button
							onClick={resetMerging}
							className="rounded-sm border border-(--rule) bg-(--paper-raised) px-3 py-1.5 text-[12px] text-(--ink) transition-colors hover:border-(--navy)"
							style={{ fontFamily: "var(--mono)" }}
						>
							reset
						</button>
					</div>
				</header>

				{showEditor && (
					<JsonEditPanel
						main={main}
						other={other}
						onApply={(m) => {
							setMerging(m);
							setShowEditor(false);
						}}
					/>
				)}

				{/* Column labels */}
				<div className="mb-4 hidden grid-cols-[1fr_128px_1fr] gap-4 lg:grid">
					<div className="bg-(--paper-raised) p-2 rounded-sm">
						<div className="text-xs font-bold uppercase tracking-wider text-indigo-600" >
							Data Utama
						</div>
						<div className="mt-1 text-sm text-indigo-900" style={{ fontFamily: "var(--serif)" }}>
							Adalah record yang akan diubah dan disimpan
						</div>
					</div>
					<div />
					<div className="bg-(--paper-raised) p-2 rounded-sm">
						<div className="text-xs font-bold uppercase tracking-wider text-indigo-600">
							Data Lainnya
						</div>
						<div className="mt-1 text-sm text-indigo-900" style={{ fontFamily: "var(--serif)" }}>
							Adalah record dianggap duplikasi dan akan dihapus setelah pengabungan
						</div>
					</div>
				</div>

				<main>
	{/* 
	Status: number | undefined;
	Relations: Relation[];
	Links: string[];
	*/}

					{/* Scalar: Name, BirthDate, DOD, Gender, Religion, NIS, Classes, Alias, Title, Phones */}
					<section className="mb-9">
						<SectionHeading
							title="Informasi Umum"
							note={`${scalarDiffs} isian berbeda`}
						/>
						<ScalarRow
							label="Nama Lahir"
							mainValue={merging.Name}
							compareValue={other.Name}
							onMainChange={(v) => setMerging((prev) => ({ ...prev, Name: v }))}
							onReplace={() => copyScalar("Name")}
						/>

						{(merging.BirthDate || other.BirthDate) && <ScalarRow
							label="Tanggal Lahir"
							mainValue={merging.BirthDate ? merging.BirthDate : ""}
							compareValue={other.BirthDate ? other.BirthDate : ""}
							onMainChange={(v) => setMerging((prev) => ({ ...prev, BirthDate: v }))}
							onReplace={() => copyScalar("BirthDate")}
							inputType="date"
						/>}

						{(merging.DOD || other.DOD) && <ScalarRow
							label="Info Kemation"
							mainValue={merging.DOD ? merging.DOD : ""}
							compareValue={other.DOD ? other.DOD : ""}
							onMainChange={(v) =>
								setMerging((prev) => ({
									...prev,
									DOD: v,
								}))
							}
							onReplace={() => copyScalar("DOD")}
						/>}

						{(merging.Gender || other.Gender) && <ScalarRow
							label="Jenis Kelamin"
							mainValue={merging.Gender ? merging.Gender : ""}
							compareValue={other.Gender ? other.Gender : ""}
							onMainChange={(v) =>
								setMerging((prev) => ({
									...prev,
									Gender: v as NonNullable<Alumni["Gender"]>,
								}))
							}
							onReplace={() => copyScalar("Gender")}
						/>}

						{(merging.Religion || other.Religion) && <ScalarRow
							label="Agama"
							mainValue={merging.Religion ? merging.Religion : ""}
							compareValue={other.Religion ? other.Religion : ""}
							onMainChange={(v) =>
								setMerging((prev) => ({
									...prev,
									Religion: v as NonNullable<Alumni["Religion"]>,
								}))
							}
							onReplace={() => copyScalar("Religion")}
						/>}

						{(merging.NIS || other.NIS) && <ScalarRow
							label="NIS"
							mainValue={merging.NIS ? merging.NIS.toString() : ""}
							compareValue={other.NIS ? other.NIS.toString() : ""}
							onMainChange={(v) =>
								setMerging((prev) => ({
									...prev,
									NIS: v.length > 0 ? Number(v) : undefined,
								}))
							}
							onReplace={() => copyScalar("NIS")}
						/>}

						{(merging.Classes || other.Classes) && <ScalarRow
							label="Kelas"
							mainValue={merging.Classes ? merging.Classes.toString() : ""}
							compareValue={other.Classes ? other.Classes.toString() : ""}
							onMainChange={(v) =>
								setMerging((prev) => ({
									...prev,
									Classes: v,
								}))
							}
							onReplace={() => copyScalar("Classes")}
						/>}

						<ScalarRow
							label="Panggilan"
							mainValue={merging.Alias ? merging.Alias : ""}
							compareValue={other.Alias ? other.Alias : ""}
							onMainChange={(v) =>
								setMerging((prev) => ({
									...prev,
									Alias: v,
								}))
							}
							onReplace={() => copyScalar("Alias")}
						/>

						<ScalarRow
							label="Title/Gelar"
							mainValue={merging.Title ? merging.Title : ""}
							compareValue={other.Title ? other.Title : ""}
							onMainChange={(v) =>
								setMerging((prev) => ({
									...prev,
									Title: v,
								}))
							}
							onReplace={() => copyScalar("Title")}
						/>

						{(merging.Phones || other.Phones) && <ScalarRow
							label="Handphone"
							mainValue={merging.Phones ? merging.Phones : ""}
							compareValue={other.Phones ? other.Phones : ""}
							onMainChange={(v) =>
								setMerging((prev) => ({
									...prev,
									Phones: v,
								}))
							}
							onReplace={() => copyScalar("Phones")}
							onMerge={mergePhones}
						/>}

						{(merging.Privacies || other.Privacies) && (merging.Privacies !== other.Privacies) && <ScalarRow
							label="Privacies"
							mainValue={merging.Privacies ? merging.Privacies : ""}
							compareValue={other.Privacies ? other.Privacies : ""}
							onMainChange={(v) =>
								setMerging((prev) => ({
									...prev,
									Privacies: v,
								}))
							}
							onReplace={() => copyScalar("Privacies")}
						/>}
					</section>

					{/* Addresses */}
					{(merging.Addresses.length > 0 || other.Addresses.length > 0) && <ArraySection<Address>
						title="Alamat"
						mainItems={merging.Addresses}
						compareItems={other.Addresses}
						renderSummary={renderAddress}
						renderEditable={renderAddressEditable}
						onMergeSelected={mergeAddresses}
						onReplaceAll={() =>
							setMerging((prev) => ({ ...prev, Addresses: cloneRecord(other).Addresses }))
						}
						onRemoveFromMain={(id) =>
							setMerging((prev) => ({ ...prev, Addresses: prev.Addresses.filter((a) => a.Id !== id) }))
						}
						onUpdateItem={(id, patch) =>
							setMerging((prev) => ({
								...prev,
								Addresses: prev.Addresses.map((a) => (a.Id === id ? { ...a, ...patch } : a)),
							}))
						}
						onAddBlank={() =>
							setMerging((prev) => ({ ...prev, Addresses: [...prev.Addresses, makeBlankAddress(prev)] }))
						}
						addLabel="tambah alamat"
					/>}

					{/* Academics */}
					{(merging.Academics.length > 0 || other.Academics.length > 0) && <ArraySection<Academic>
						title="Pendidikan"
						mainItems={merging.Academics}
						compareItems={other.Academics}
						renderSummary={renderAcademic}
						renderEditable={renderAcademicEditable}
						onMergeSelected={mergeAcademics}
						onReplaceAll={() =>
							setMerging((prev) => ({ ...prev, Academics: cloneRecord(other).Academics }))
						}
						onRemoveFromMain={(id) =>
							setMerging((prev) => ({ ...prev, Academics: prev.Academics.filter((a) => a.Id !== id) }))
						}
						onUpdateItem={(id, patch) =>
							setMerging((prev) => ({
								...prev,
								Academics: prev.Academics.map((a) => (a.Id === id ? { ...a, ...patch } : a)),
							}))
						}
						onAddBlank={() =>
							setMerging((prev) => ({ ...prev, Academics: [...prev.Academics, makeBlankAcademic(prev)] }))
						}
						addLabel="tambah pendidikan"
					/>}

					{/* Businesses */}
					{(merging.Businesses.length > 0 || other.Businesses.length > 0) && <ArraySection<Business>
						title="Karir/Pekerjaan"
						mainItems={merging.Businesses}
						compareItems={other.Businesses}
						renderSummary={renderBusiness}
						renderEditable={renderBusinessEditable}
						onMergeSelected={mergeBusiness}
						onReplaceAll={() =>
							setMerging((prev) => ({ ...prev, Businesses: cloneRecord(other).Businesses }))
						}
						onRemoveFromMain={(id) =>
							setMerging((prev) => ({
								...prev,
								Businesses: prev.Businesses.filter((h) => h.Id !== id),
							}))
						}
						onUpdateItem={(id, patch) =>
							setMerging((prev) => ({
								...prev,
								Businesses: prev.Businesses.map((h) => (h.Id === id ? { ...h, ...patch } : h)),
							}))
						}
						onAddBlank={() =>
							setMerging((prev) => ({
								...prev,
								Businesses: [...prev.Businesses, makeBlankBusiness(prev)],
							}))
						}
						addLabel="tambah karir/pekerjaan"
					/>}

					{/* WebUsers */}
					{(merging.WebUsers.length > 0 || other.WebUsers.length > 0) && <ArraySection<WebUser>
						title="Email Logins"
						mainItems={merging.WebUsers}
						compareItems={other.WebUsers}
						renderSummary={renderWebUsers}
						renderEditable={renderWebUsersEditable}
						onMergeSelected={mergeWebUsers}
						onReplaceAll={() =>
							setMerging((prev) => ({ ...prev, WebUsers: cloneRecord(other).WebUsers }))
						}
						onRemoveFromMain={(id) =>
							setMerging((prev) => ({
								...prev,
								WebUsers: prev.WebUsers.filter((h) => h.Id !== id),
							}))
						}
						onUpdateItem={(id, patch) =>
							setMerging((prev) => ({
								...prev,
								WebUsers: prev.WebUsers.map((h) => (h.Id === id ? { ...h, ...patch } : h)),
							}))
						}
						onAddBlank={() => { }}
						addLabel="tambah logins"
					/>}

					{/* History */}
					<HistorySection
						title="Change Histories"
						mainXD={merging.ExtraData}
						otherXD={other.ExtraData}
						onMerge={mergeHistory}
					/>
				</main>

				{/* Merged result preview */}
				<section className="mt-10 rounded-sm border border-(--rule) bg-(--paper-raised) p-4">
					<div className="mb-3 flex items-center justify-between gap-3">
						<h2
							className="text-[15px] text-(--ink)"
							style={{ fontFamily: "var(--serif)" }}
						>
							Hasil data pengabungan
						</h2>
						<button
							onClick={() => setShowResultJson((v) => !v)}
							className="rounded-sm border border-(--rule) bg-(--paper) px-2.5 py-1 text-[11px] text-(--ink-soft) transition-colors hover:border-(--navy) hover:text-(--navy)"
							style={{ fontFamily: "var(--mono)" }}
						>
							{showResultJson ? "sembunyikan JSON" : "lihat JSON"}
						</button>
					</div>
					{showResultJson && (
						<pre
							className="mb-4 max-h-80 overflow-auto rounded-sm bg-(--paper) p-3 text-[11.5px] leading-relaxed text-(--ink)"
							style={{ fontFamily: "var(--mono)" }}
						>
							{JSON.stringify(merging, null, 2)}
						</pre>
					)}
					<div className="flex justify-end gap-2 mt-2">
						<button
							onClick={onCancel}
							className="rounded-sm border border-(--rule) bg-(--paper) px-3 py-1.5 text-[11.5px] font-semibold text-(--ink) transition-colors hover:border-(--navy) hover:text-(--navy) focus:outline-none focus:ring-2 focus:ring-(--navy)/30"
							style={{ fontFamily: "var(--mono)" }}
						>
							Cancel
						</button>
						<button
							onClick={() => onMerge?.(main, merging, other)}
							disabled={!onMerge}
							className="rounded-sm border border-red-600 bg-red-600 px-3 py-1.5 text-[11.5px] font-semibold text-white transition-colors hover:bg-red-700 hover:border-red-700 focus:outline-none focus:ring-2 focus:ring-red-500/40 disabled:cursor-not-allowed disabled:opacity-50"
							style={{ fontFamily: "var(--mono)" }}
						>
							GABUNG RECORD
						</button>
					</div>
				</section>
			</div>
		</div>
	);
}